import { useMemo, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'

import { ActStrip, useConfirmAfterApplied } from './ActStrip.jsx'
import AvatarCircle from './AvatarCircle.jsx'
import { PosterSlot } from './WatchPanel.jsx'
import {
  MISCONFIGURED as ARCHIVE_MISCONFIGURED,
  READY as ARCHIVE_READY,
  UNAVAILABLE as ARCHIVE_UNAVAILABLE,
  titlesOf,
  useWatchArchive,
  yourArchiveEntry,
} from './watchArchive.js'
import { hueForTitle, joinableId, monthYearLabel, READY as WATCH_READY, useWatch } from './watch.js'
import { gameOf, memberBadges, memberStatTiles } from './watchGame.js'
import {
  favoriteMoveTarget,
  memberFavorites,
  memberFinishedCount,
  memberLogEntries,
  memberReviews,
  memberSince,
} from './watchMember.js'
import {
  KIND_SET_FAVORITE_RANK,
  TARGET_WATCHED,
  favoriteRankCommand,
  useWatchRequest,
} from './watchRequests.js'
import { LOADING as SESSION_LOADING, SIGNED_IN, signInHref, useSession } from '../session.js'
import { UNKNOWN_NAME, nameFor, useDirectory } from '../directory.js'
import { useDocumentTitle } from '../useDocumentTitle.js'

// The member profile — screen 5 of docs/design-briefs/watch/v2/HANDOFF.md,
// "the show-off page." V2-2c.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 NO PER-MEMBER ENDPOINT EXISTS, AND THIS SLICE DOES NOT ADD ONE —
//    `site/functions/**` IS OUTSIDE ITS OWNERSHIP
// ═══════════════════════════════════════════════════════════════════════════
//
// Everything on this page is derived from data three OTHER already-signed-
// in-gated fetches already publish: the archive (`watchArchive.js`, every
// title anyone has ever touched, listed or not — the one COMPLETE source
// for a member's own favourites/log/reviews, since all three can live on a
// title off the watchlist), `/api/watch` (`watch.js`, listed subscriptions
// only — read purely to ENRICH a listed title's per-member position, the
// exact "borrow, never replace" shape `watchArchive.js::yourStatusLine`
// already established one screen over), and the member directory
// (`../directory.js`, for a name and a Discord avatar). See
// `watchMember.js`'s own header for the full reasoning, including why a
// routed id that matches nothing here renders an honestly EMPTY profile
// rather than a bespoke "not found" state.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 REACHED FROM INSIDE THE TAB, NEVER A "ME" BUTTON — RECONCILIATION §1
// ═══════════════════════════════════════════════════════════════════════════
//
// There is no separate route or redirect for "my own profile" — clicking
// your own chip or avatar (wherever one renders, on the title page, a
// watchlist row, or here) links to this exact route with your own id,
// through the SAME `watch.js::memberProfilePath` every other member's chip
// uses. `isOwnProfile` below is computed once the page has actually loaded
// (`memberId === session.userId`), never assumed from how the page was
// reached.
//
// ═══════════════════════════════════════════════════════════════════════════
// STAT TILES ARRIVED WITH V2-5c; BADGES ARRIVE HERE, WITH V2-5f.
// ═══════════════════════════════════════════════════════════════════════════
//
// The five tiles (Hours, Finished, Episodes, Reviews, Streak — HANDOFF.md
// §5's own order, all-time) read `game.members[id].all_time` off the SAME
// archive blob this page already fetches (`watchArchive.js`'s own `game`
// key — see `watchGame.js`'s header for why it rides that blob rather than
// a fourth fetch). `gameOf`/`memberStatTiles` (`watchGame.js`) are the same
// selectors `WatchLeaderboards.jsx` uses for the board and the hall of
// fame — one contract, two renderers, pinned against the bot's own
// `compute_watch_game` by `tests/test_watch_game_fixture.py`'s shared
// fixture (CLAUDE.md gotcha 21).
//
// A `game` published before V2-5a (or not at all) omits the tiles section
// ENTIRELY here — never a row of zeros that would read as "this member has
// done nothing" when the truth is "this feature is not live yet." The rest
// of the profile (favourites, log, reviews) does not depend on `game` at
// all, so its absence costs this ONE section, not the page.
//
// Badges (HANDOFF §5: "pills with a pink 1px border and an orange star")
// read `game.members[id].badges` the identical way, through `watchGame.js::
// memberBadges` — this section used to say "badges still draw nothing:
// `game` carries no badge data at all," true until V2-5d published
// `game["badges"]` / `game.members[id].badges`. A member with none gets no
// pills and no box at all (brief §0), the same "omit the section, never a
// fabricated empty one" rule the stat tiles already follow one paragraph up.

export default function WatchMemberProfile() {
  const { memberId } = useParams()
  return <WatchMemberProfileBody key={memberId} memberId={memberId} />
}

function WatchMemberProfileBody({ memberId }) {
  const location = useLocation()
  const session = useSession()
  const signedIn = session.status === SIGNED_IN
  const archive = useWatchArchive(signedIn)
  const watch = useWatch(signedIn)
  const directory = useDirectory()

  const person = nameFor(directory, memberId)
  useDocumentTitle(person.known ? person.label : 'Member')

  if (session.status === SESSION_LOADING) {
    return (
      <Frame>
        <p className="d2d-notice" data-testid="profile-loading-session">
          Checking whether you are signed in.
        </p>
      </Frame>
    )
  }

  if (!signedIn) {
    return (
      <Frame>
        <p className="d2d-notice" data-testid="profile-signed-out">
          You need to be signed in to see this. <a href={signInHref(location)}>Sign in with Discord</a>.
        </p>
      </Frame>
    )
  }

  if (archive.state === ARCHIVE_MISCONFIGURED) {
    return (
      <Frame>
        <p className="d2d-notice" data-testid="profile-misconfigured">
          The archive is set up wrong on this deployment, so this profile cannot be shown until
          that is fixed — this is not about your account.
        </p>
      </Frame>
    )
  }

  if (archive.state !== ARCHIVE_READY) {
    return (
      <Frame>
        <p
          className="d2d-notice"
          data-testid={archive.state === ARCHIVE_UNAVAILABLE ? 'profile-unavailable' : 'profile-loading'}
        >
          {archive.state === ARCHIVE_UNAVAILABLE
            ? 'This profile is unavailable right now. Try again in a minute.'
            : 'Loading the profile…'}
        </p>
      </Frame>
    )
  }

  return (
    <Frame>
      <ProfileBody
        archiveShared={archive.shared}
        watchShared={watch.state === WATCH_READY ? watch.shared : null}
        confirmFavorite={archive.confirm}
        directory={directory}
        memberId={memberId}
        viewerUserId={session.userId}
      />
    </Frame>
  )
}

function Frame({ children }) {
  return (
    <article className="d2d-page d2d-watch d2d-profile" data-testid="watch-profile-page">
      <p className="d2d-watchtitle__back">
        <Link to="/">← Watch</Link>
      </p>
      {children}
    </article>
  )
}

function ProfileBody({ archiveShared, watchShared, confirmFavorite, directory, memberId, viewerUserId }) {
  const isOwnProfile = memberId === viewerUserId
  const person = nameFor(directory, memberId)
  const initial = person.known ? person.label.trim().charAt(0).toUpperCase() : '·'

  const favoritesAll = useMemo(() => memberFavorites(archiveShared, memberId), [archiveShared, memberId])
  // The design's own cap — "up to 10." A member's own reorder buttons only
  // ever act within this visible slice (see `favoriteMoveTarget`'s own
  // header); a title ranked 11th or lower cannot be dragged into view from
  // this page, stated in the report rather than silently limited.
  const favorites = favoritesAll.slice(0, 10)
  const finishedCount = useMemo(() => memberFinishedCount(archiveShared, memberId), [archiveShared, memberId])
  const since = useMemo(() => memberSince(archiveShared, memberId), [archiveShared, memberId])
  const log = useMemo(
    () => memberLogEntries(archiveShared, watchShared, memberId),
    [archiveShared, watchShared, memberId],
  )
  const reviews = useMemo(
    () => memberReviews(archiveShared, watchShared, memberId),
    [archiveShared, watchShared, memberId],
  )

  const topFavorite = favorites[0]?.title ?? null
  const secondFavorite = favorites[1]?.title ?? null
  const hue1 = topFavorite ? hueForTitle(topFavorite) : null
  const hue2 = secondFavorite ? hueForTitle(secondFavorite) : null
  const washed = hue1 !== null

  const heroStyle = washed ? { '--d2d-profile-hue-1': hue1, '--d2d-profile-hue-2': hue2 ?? hue1 } : undefined
  const avatarModifier = isOwnProfile
    ? ' d2d-profile__avatar--you'
    : washed
      ? ' d2d-profile__avatar--hued'
      : ''

  const favMeta =
    favoritesAll.length === 0
      ? null
      : favoritesAll.length <= favorites.length
        ? `${favoritesAll.length} of 10`
        : `showing 10 of ${favoritesAll.length}`

  const game = useMemo(() => gameOf(archiveShared), [archiveShared])
  const tiles = useMemo(() => (game ? memberStatTiles(game, memberId) : []), [game, memberId])
  const badges = useMemo(() => memberBadges(game, memberId), [game, memberId])

  return (
    <div className="d2d-profile__body">
      <section
        className={`d2d-profile__hero${washed ? ' d2d-profile__hero--washed' : ''}`}
        data-testid="profile-hero"
        style={heroStyle}
      >
        <div className="d2d-profile__herorow">
          <AvatarCircle
            person={person}
            initial={initial}
            className={`d2d-avatar d2d-avatar--88 d2d-profile__avatar${avatarModifier}`}
            imgClassName="d2d-avatarimg"
            testId="profile-avatar"
          />
          <div className="d2d-profile__heroinfo">
            <div className="d2d-micro d2d-profile__kicker">{isOwnProfile ? 'You' : 'Member'}</div>
            <h1
              className={`d2d-profile__name${isOwnProfile ? ' d2d-profile__name--you' : ''}`}
              data-testid="profile-name"
            >
              {person.known ? person.label : UNKNOWN_NAME}
            </h1>
            <p className="d2d-profile__meta" data-testid="profile-meta">
              {since ? `Member since ${since} · ` : ''}
              {finishedCount} finished all time
            </p>
          </div>
        </div>
        {topFavorite ? (
          <p className="d2d-micro d2d-profile__topfav" data-testid="profile-number-one">
            Number one · <span className="d2d-profile__topfavtitle">{topFavorite.title}</span>
          </p>
        ) : null}
      </section>

      {tiles.length > 0 ? (
        <ul className="d2d-profile__stattiles" aria-label="Stats, all time" data-testid="profile-stat-tiles">
          {tiles.map((tile) => (
            <li
              key={tile.metric}
              className={`d2d-profile__stattile d2d-profile__stattile--${tile.metric}`}
              data-testid={`profile-stat-tile-${tile.metric}`}
            >
              <span className="d2d-micro d2d-profile__stattilelabel">{tile.label}</span>
              <span className="d2d-profile__stattilevalue">{tile.value}</span>
            </li>
          ))}
        </ul>
      ) : null}

      {badges.length > 0 ? (
        <ul className="d2d-profile__badgepills" aria-label="Badges" data-testid="profile-badges">
          {badges.map((name) => (
            <li key={name} className="d2d-micro d2d-profile__badgepill" data-testid="profile-badge-pill">
              <span aria-hidden="true" className="d2d-profile__badgestar">
                ★
              </span>{' '}
              {name}
            </li>
          ))}
        </ul>
      ) : null}

      <section className="d2d-watch__section" aria-label="Favorites" data-testid="profile-favorites">
        <div className="d2d-watchtitle__sectionhead">
          <span className="d2d-micro d2d-watchtitle__sectiontitle">Favorites · in order</span>
          {favMeta ? <span className="d2d-micro d2d-watch__note">{favMeta}</span> : null}
        </div>
        {favorites.length === 0 ? (
          <p className="d2d-watch__flatline" data-testid="profile-no-favorites">
            No favorites yet.
          </p>
        ) : (
          <ol className="d2d-profile__favlist">
            {favorites.map((favorite, index) => (
              <FavoriteRow
                key={favorite.title.id}
                favorite={favorite}
                index={index}
                favorites={favorites}
                canMove={isOwnProfile}
                memberId={memberId}
                onChanged={confirmFavorite}
              />
            ))}
          </ol>
        )}
      </section>

      <div className="d2d-profile__grid">
        <section className="d2d-watch__section" aria-label="Log" data-testid="profile-log">
          <span className="d2d-micro d2d-watchtitle__sectiontitle">Log</span>
          {log.length === 0 ? (
            <p className="d2d-watch__flatline" data-testid="profile-no-log">
              Nothing logged yet.
            </p>
          ) : (
            <ul className="d2d-profile__logrows">
              {log.map((entry) => (
                <li key={entry.id}>
                  <Link
                    className="d2d-profile__logrow"
                    to={`/watch/${entry.id}`}
                    data-testid="profile-log-row"
                    style={{ '--d2d-title-hue': entry.hue }}
                  >
                    <PosterSlot subscription={{ poster_url: entry.posterUrl }} size="log" />
                    <span
                      className={`d2d-micro ${entry.current ? 'd2d-profile__logwhen--live' : 'd2d-profile__logwhen'}`}
                    >
                      {entry.when}
                    </span>
                    <span className="d2d-profile__logtitle">{entry.title}</span>
                    <span className="d2d-micro d2d-profile__logdetail">{entry.detail}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="d2d-watch__section" aria-label="Reviews" data-testid="profile-reviews">
          <span className="d2d-micro d2d-watchtitle__sectiontitle">Reviews</span>
          <ProfileReviews reviews={reviews} viewerUserId={viewerUserId} isOwnProfile={isOwnProfile} />
        </section>
      </div>
    </div>
  )
}

/** One favourite — rank + poster + title, always; the ↑/↓ move control only
 * on your own profile (brief §2.4: "on anyone else's profile, no buttons").
 * Split from `FavoriteMoveButtons` below rather than branching inside one
 * component so the move control's own `useWatchRequest()` is never called
 * on a profile that cannot use it — a hook that runs unconditionally is
 * simpler to reason about than one gated by a prop check inside itself. */
function FavoriteRow({ favorite, index, favorites, canMove, memberId, onChanged }) {
  const { title, rank } = favorite
  const podium = rank <= 3
  const hue = hueForTitle(title)

  return (
    <li className="d2d-profile__favrow" data-testid="profile-favorite-row" style={{ '--d2d-title-hue': hue }}>
      <span className={`d2d-profile__favrank${podium ? ' d2d-profile__favrank--podium' : ''}`}>{rank}</span>
      <PosterSlot subscription={title} size={podium ? 'fav-podium' : 'fav-rest'} />
      <Link className="d2d-profile__favtitle" to={`/watch/${title.id}`}>
        {title.title}
      </Link>
      {canMove ? (
        <FavoriteMoveButtons favorite={favorite} index={index} favorites={favorites} memberId={memberId} onChanged={onChanged} />
      ) : null}
    </li>
  )
}

/**
 * The reorder control — `watch.set_favorite_rank` through `ActStrip`'s six
 * states, confirm-after-applied against a FRESH archive (a favourite can
 * be a title off the watchlist, so `/api/watch`'s own confirm cannot see
 * it — `onChanged` is `useWatchArchive`'s own new `confirm`, V2-2c).
 *
 * The target rank sent is the NEIGHBOUR's own current rank
 * (`favoriteMoveTarget`, `watchMember.js`) — an ordinary adjacent swap,
 * per `WatchManager.set_favorite_rank`'s own reorder rule (see that
 * function's docstring, quoted in the report).
 *
 * 🔴 A favourite can be a title the crew has since DROPPED from the
 * watchlist. `cogs/watch/requests.py::_evaluate` refuses every request
 * kind — including this one — against an unlisted title
 * (`REFUSAL_NOT_LISTED`, "That title isn't on the crew's list.") before it
 * ever reaches the favourite-rank handler. This control does not special-
 * case that: pressing ↑/↓ on such a favourite reaches the ordinary REFUSED
 * state with the bot's own honest sentence, rather than a button that
 * silently declines to render — "fail loud, never degrade silently"
 * applies to a button as much as to a store. See the report.
 */
function FavoriteMoveButtons({ favorite, index, favorites, memberId, onChanged }) {
  const { title } = favorite
  const request = useWatchRequest()
  const [askedRank, setAskedRank] = useState(null)
  const upTarget = favoriteMoveTarget(favorites, index, -1)
  const downTarget = favoriteMoveTarget(favorites, index, 1)

  const isConfirmed = (shared) => {
    const fresh = titlesOf(shared).find((row) => row.id === title.id)
    const members = Array.isArray(fresh?.members) ? fresh.members : []
    const mine = members.find((member) => joinableId(member?.id) === memberId)
    return mine?.favorite_rank === askedRank
  }
  const confirmFailed = useConfirmAfterApplied(request, onChanged, isConfirmed)

  const press = (rank) => {
    if (rank === null) return
    setAskedRank(rank)
    request.ask({ kind: KIND_SET_FAVORITE_RANK, target: title.id, payload: { rank } })
  }

  if (request.status) {
    return (
      <ActStrip
        request={request}
        confirmFailed={confirmFailed}
        testId="profile-favorite-status"
        confirmStaleTestId="profile-favorite-confirm-stale"
        doneText="Your favorites are reordered."
        command={favoriteRankCommand(title.title, askedRank)}
        onRetry={askedRank !== null ? () => press(askedRank) : undefined}
      />
    )
  }

  return (
    <div className="d2d-profile__favmove">
      {upTarget !== null ? (
        <button
          type="button"
          aria-label={`Move ${title.title} up`}
          className="d2d-profile__favbtn"
          data-testid="profile-favorite-up"
          onClick={() => press(upTarget)}
        >
          ↑
        </button>
      ) : (
        <span className="d2d-profile__favbtn d2d-profile__favbtn--placeholder" aria-hidden="true" />
      )}
      {downTarget !== null ? (
        <button
          type="button"
          aria-label={`Move ${title.title} down`}
          className="d2d-profile__favbtn"
          data-testid="profile-favorite-down"
          onClick={() => press(downTarget)}
        >
          ↓
        </button>
      ) : (
        <span className="d2d-profile__favbtn d2d-profile__favbtn--placeholder" aria-hidden="true" />
      )}
    </div>
  )
}

/** The member's own reviews, across every title — `reviewDisclaimer` and
 * the blur/reveal mechanic reused from `WatchTitle.jsx::ReviewRow`
 * (V2-2b), never retyped; the VISUAL shape differs on purpose (a title's
 * poster, not the reviewer's own avatar — the reviewer IS this whole page,
 * so repeating their name/picture on every row would be the redundant
 * half of the design's own layout, and HANDOFF §5 draws a poster here,
 * never a circle). See the report for this reuse-vs-copy decision. */
function ProfileReviews({ reviews, viewerUserId, isOwnProfile }) {
  const [revealed, setRevealed] = useState(() => new Set())

  if (reviews.length === 0) {
    return (
      <p className="d2d-watch__flatline" data-testid="profile-no-reviews">
        No reviews yet.
      </p>
    )
  }

  return (
    <>
      {reviews.map((review) => {
        const viewerFinished =
          isOwnProfile || yourArchiveEntry(review.archiveTitle, viewerUserId)?.status === TARGET_WATCHED
        const blurred = !viewerFinished && !revealed.has(review.id)
        return (
          <div
            key={review.id}
            className="d2d-profile__reviewrow"
            data-testid="profile-review-row"
            style={{ '--d2d-title-hue': review.hue }}
          >
            <Link className="d2d-profile__reviewhead" to={`/watch/${review.id}`}>
              <PosterSlot subscription={{ poster_url: review.posterUrl }} size="review" />
              <span className="d2d-profile__reviewbody">
                <span className="d2d-profile__reviewtitle">{review.title}</span>
                <span className="d2d-micro d2d-watch__note">
                  {review.whenMs === null ? '' : monthYearLabel(review.whenMs)}
                </span>
              </span>
              <span className="d2d-profile__reviewscore">
                <span className="d2d-profile__reviewnum">{review.rating ?? '–'}</span>
                <span className="d2d-micro">/10</span>
              </span>
            </Link>

            {review.disclaimer ? (
              <div className="d2d-watchtitle__dnf" role="note" data-testid="profile-review-dnf">
                <div className="d2d-watchtitle__dnfhead">Didn’t finish</div>
                <div className="d2d-micro d2d-watchtitle__dnfline">{review.disclaimer}</div>
              </div>
            ) : null}

            {blurred ? (
              <div className="d2d-watchtitle__reviewblurwrap">
                <p
                  aria-hidden="true"
                  className="d2d-watchtitle__reviewtext d2d-watchtitle__reviewtext--blurred"
                  data-testid="profile-review-text-blurred"
                >
                  {review.text}
                </p>
                <button
                  type="button"
                  className="d2d-watchtitle__revealreview"
                  onClick={() => setRevealed((set) => new Set(set).add(review.id))}
                  data-testid="profile-review-reveal"
                >
                  Reveal · may spoil it
                </button>
              </div>
            ) : (
              <p className="d2d-watchtitle__reviewtext" data-testid="profile-review-text">
                {review.text}
              </p>
            )}
          </div>
        )
      })}
    </>
  )
}
